import { Button, Typography } from '@material-ui/core'
import i18n from 'i18next'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { FC, Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../../actions/dialogs'
import { Apartment, ApartmentStatus } from '../../../types/apartments'
import { getFiksumyyntiAppUrl, hasSaleListing } from './fiksumyyntiUrl'
import SellOnFiksumyyntiDialog, {
  SELL_ON_FIKSUMYYNTI_DIALOG_ID
} from './SellOnFiksumyyntiDialog'

interface SellOnFiksumyyntiCardProps {
  t: i18n.TFunction
  apartment: Apartment
  buttonClassName?: string
}

const benefitKeys = ['noStartFee', 'visibility', 'quick']

/**
 * Overview'n ristiinmyynti: kortti myynti-ilmoituksen luontiin, tai linkityksen
 * jälkeen pelkkä maininta — julkaistusta myynnistä ei puhuta FV:ssä enempää.
 */
const SellOnFiksumyyntiCard: FC<SellOnFiksumyyntiCardProps> = ({
  t,
  apartment,
  buttonClassName
}) => {
  const dispatch = useDispatch()
  const [publishedNow, setPublishedNow] = useState(false)
  const isLinked = publishedNow || hasSaleListing(apartment)

  // Dialogi renderöidään aina samaan paikkaan Fragmentin toisena lapsena. Jos se on eri
  // haaroissa eri kohdassa elementtipuuta, React purkaa ja rakentaa sen uudestaan julkaisun
  // jälkeen (kortti → linkitetty-huomautus), published-tila katoaa ja Redux pitää dialogin
  // auki → dialogi aukesi vaiheeseen 1 vaikka julkaisu onnistui (löydös 23.10.)
  const dialog = (
    <SellOnFiksumyyntiDialog
      t={t}
      apartment={apartment}
      onPublished={() => setPublishedNow(true)}
    />
  )

  // Myyntiä tarjotaan vasta julkaistulle vuokrailmoitukselle
  // Fiksumyynnissä myydään vain asunto-osakeyhtiön huoneistoja: FV:n omakotitalo on
  // kiinteistö, jolle myyntiä ei tarjota lainkaan (päätös 23.10.)
  const isDetachedHouse = apartment.information?.buildingType === 'detached'
  const showCard =
    !isLinked && !isDetachedHouse && apartment.status === ApartmentStatus.marketing

  return (
    <Fragment>
      {isLinked ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            margin: '16px 40px 0 0'
          }}
        >
          <CheckCircleOutlineIcon
            size={20}
            color='#4f9b44'
            style={{ flex: 'none' }}
          />
          <Typography variant='body2'>
            {t('apartmentOverview.sellOnFiksumyynti.linkedNote')}{' '}
            <a
              href={getFiksumyyntiAppUrl()}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                color: '#4f9b44',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              {t('apartmentOverview.sellOnFiksumyynti.linkedLink')}
              <OpenInNewIcon size={14} />
            </a>
          </Typography>
        </div>
      ) : showCard ? (
        <div
          style={{
            marginTop: 24,
            marginRight: 40,
            border: '1px solid #4f9b44',
            background: '#f5faf4',
            borderRadius: 4,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }}
        >
          <Typography
            style={{
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.02857em',
              color: '#3f7c36'
            }}
          >
            {t('apartmentOverview.sellOnFiksumyynti.card.eyebrow')}
          </Typography>
          <Typography
            style={{
              fontFamily: 'CircularBold',
              fontSize: 20,
              lineHeight: 1.3,
              marginTop: -6
            }}
          >
            {t('apartmentOverview.sellOnFiksumyynti.card.title')}
          </Typography>
          <Typography variant='body1'>
            {t('apartmentOverview.sellOnFiksumyynti.card.body')}
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {benefitKeys.map((key) => (
              <div
                key={key}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
              >
                <CheckCircleOutlineIcon
                  size={22}
                  color='#4f9b44'
                  style={{ flex: 'none' }}
                />
                <Typography variant='body1'>
                  {t(
                    `apartmentOverview.sellOnFiksumyynti.card.benefits.${key}`
                  )}
                </Typography>
              </div>
            ))}
          </div>
          <Typography variant='body1'>
            {t('apartmentOverview.sellOnFiksumyynti.card.fee')}
          </Typography>
          <div>
            <Button
              size='small'
              className={buttonClassName}
              style={{ padding: '9px 16px' }}
              onClick={() =>
                dispatch(openDialog(SELL_ON_FIKSUMYYNTI_DIALOG_ID))
              }
            >
              {t('apartmentOverview.sellOnFiksumyynti.card.cta')}
            </Button>
          </div>
        </div>
      ) : null}
      {dialog}
    </Fragment>
  )
}

export default SellOnFiksumyyntiCard
